/**
 * Zeilen mit der Maus verschieben — der fachfreie Teil (AP-32 Nachtrag 8, N-71).
 *
 * Zuerst gebaut für die Stückliste (AP-35 Nachtrag 6), gedacht für jede Liste
 * aus Blöcken: das Positionsraster im Belegwesen, Verkauf, Arbeitsschritte. Das
 * Fach liefert nur zwei Dinge — welche Blöcke eine Ebene bilden (`[data-key]`
 * im Behälter) und was beim Ablegen geschieht (`onAblegen`).
 *
 * Seit 19.9.2026 (N-64 neu) greift man die Zeile an ihrer freien Fläche statt
 * an einem Griff, markiert sie am linken Rand und zieht mehrere Markierte auf
 * einmal (N-69 neu).
 */

export interface Block {
  key: string
  oben: number
  unten: number
}

/**
 * Vor welchem Block abgelegt wird: vor dem ersten, dessen **Mitte** unter dem
 * Zeiger liegt; `null` heißt ans Ende. Die Zielbestimmung hängt an der
 * Zeigerhöhe, nicht an der Kollisionserkennung von dnd-kit (N-66, N-70) —
 * Blöcke unterschiedlicher Höhe verhalten sich so vorhersehbar.
 */
export function zielAusZeiger(bloecke: readonly Block[], y: number): string | null {
  for (const b of bloecke) if (y < (b.oben + b.unten) / 2) return b.key
  return null
}

/**
 * Die neue Reihenfolge, wenn `key` vor `vorKey` (bzw. ans Ende) wandert.
 * Gleiche Stelle → dieselbe Referenz, damit der Aufrufer „nichts geändert“
 * erkennt (N-72).
 */
export function verschiebeVor<T extends string>(reihe: readonly T[], key: T, vorKey: T | null): readonly T[] {
  return verschiebeMehrereVor(reihe, [key], vorKey)
}

/**
 * Mehrere Zeilen auf einmal (N-69 neu): Sie wandern **geschlossen und in ihrer
 * bisherigen Reihenfolge** vor `vorKey`, Lücken zwischen ihnen schließen sich.
 * Ist `vorKey` selbst eine der gezogenen, gilt die nächste nicht gezogene
 * danach. Gleiche Reihenfolge → dieselbe Referenz.
 */
export function verschiebeMehrereVor<T extends string>(reihe: readonly T[], keys: readonly T[], vorKey: T | null): readonly T[] {
  const menge = new Set(keys.filter((k) => reihe.includes(k)))
  if (!menge.size) return reihe
  let vor = vorKey
  if (vor !== null && menge.has(vor)) vor = reihe.slice(reihe.indexOf(vor) + 1).find((k) => !menge.has(k)) ?? null
  const ohne = reihe.filter((k) => !menge.has(k))
  const nach = vor === null ? ohne.length : ohne.indexOf(vor)
  if (nach < 0) return reihe
  const neu = [...ohne.slice(0, nach), ...reihe.filter((k) => menge.has(k)), ...ohne.slice(nach)]
  return neu.every((k, i) => k === reihe[i]) ? reihe : neu
}

/** Alle Schlüssel von `a` bis `b` einschließlich, gleich in welcher Richtung. */
export function bereich<T extends string>(reihe: readonly T[], a: T, b: T): T[] {
  const i = reihe.indexOf(a)
  const j = reihe.indexOf(b)
  if (i < 0 || j < 0) return j < 0 ? [] : [b]
  return reihe.slice(Math.min(i, j), Math.max(i, j) + 1)
}

/**
 * Klick auf den linken Rand (N-64 neu), wie in Explorer und Tabellenkalkulation:
 * `neu` markiert nur diese Zeile, `umschalten` (Strg) nimmt sie dazu oder weg,
 * `bereich` (Umschalt) markiert vom Anker bis hier und ersetzt die Markierung.
 */
export function markiereAmRand(
  reihe: readonly string[],
  markiert: ReadonlySet<string>,
  anker: string | null,
  key: string,
  art: 'neu' | 'umschalten' | 'bereich',
): Set<string> {
  if (art === 'bereich' && anker) return new Set(bereich(reihe, anker, key))
  if (art === 'umschalten') {
    const n = new Set(markiert)
    if (n.has(key)) n.delete(key)
    else n.add(key)
    return n
  }
  return new Set([key])
}
